import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBase from 'LuxKit/Modal/ModalBase'
import config from 'constants/config'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import GeoContext from 'contexts/geoContext'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import BodyText from 'LuxKit/Typography/BodyText'
import Table from 'LuxKit/Tables/Table'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import Pill from 'LuxKit/Pill'
import styled from 'styled-components'
import { rem } from 'polished'
import { getImageUrl } from 'lib/image/imageUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

const SPEND_EARN_MODAL_IMAGE_BACKGROUND = 'promo-spend-earn-background'

const SpendEarnHeader = styled(VerticalSpacer)`
  background-image: url(${getImageUrl(SPEND_EARN_MODAL_IMAGE_BACKGROUND, { brightness: -50 })});
  background-position: center;
  background-size: 100%;
  padding: ${rem(48)} 0;
`

interface Props {
  maxCredit: number
  spendEarnList: Array<{ spend: number, earn: number }>
}

function TourSpendEarnDetailModal({ maxCredit, spendEarnList }: Props) {
  const currency = useContext(GeoContext).currentCurrency
  const elementContext = useModalElementContext()
  const intl = useIntl()

  const onClose = useCallback(() => elementContext?.resolve(), [elementContext])
  const spendMoreEarnMoreTitle = intl.formatMessage(
    {
      id: 'VPsW68Hsqb3-6V8dBORF',
      defaultMessage: 'Spend more, earn more',
      description: 'Tour spend and earn detail modal title on the tour offer detail page',
    },
  )

  return <ModalBase>
    <ModalHeader
      title={spendMoreEarnMoreTitle}
      isOverlay
    />
    <ModalBody>
      <SpendEarnHeader gap={16}>
        <HeadingTextBlock colour="neutral-eight" variant="heading1" align="center">
          <FormattedMessage
            id="gKzCt3uqiGblbL0CW2Bm"
            defaultMessage="<i>Spend</i> more,<br></br><i>Earn</i> more"
            description="Hero heading in tour spend and earn modal with italic emphasis on Spend and Earn"
            values={{
              i: (chunks) => <i>{chunks}</i>,
              br: () => <br />,
            }}
          />
        </HeadingTextBlock>
        <HeadingTextBlock colour="neutral-eight" variant="heading6" align="center">
          <FormattedMessage
            id="iwkL7H-L11mbD4iiCYMN"
            defaultMessage="Use code <pill>TOURDEAL</pill> at checkout"
            description="Promo code instruction with pill badge in tour spend and earn modal"
            values={{
              pill: (chunks) => <Pill kind="primary" variant="success">{chunks}</Pill>,
            }}
          />
        </HeadingTextBlock>
      </SpendEarnHeader>
      <ModalContent>
        <VerticalSpacer gap={20}>
          <HeadingTextBlock variant="heading4">
            <FormattedMessage
              id="rXt-n_IPpt7HCKL-_iXp"
              defaultMessage="Book a tour and enjoy up to {amount} {brandName} credit"
              description="Tour spend and earn modal heading showing maximum credit amount"
              values={{
                amount: <FormatCurrency value={maxCredit} currency={currency} />,
                brandName: config.brandConfig.name,
              }}
            />
          </HeadingTextBlock>
          <Table>
            <TableBody>
              <TableBodyRow>
                <TableBodyCell>
                  <BodyText variant="medium" weight="bold">
                    {intl.formatMessage(
                      {
                        id: '1KkgfMNmeJmaFRHTnkji',
                        defaultMessage: 'Total spend',
                        description: 'Table header for total spend amount in tour spend and earn modal',
                      },
                    )}
                  </BodyText>
                </TableBodyCell>
                <TableBodyCell>
                  <BodyText variant="medium" weight="bold">
                    {intl.formatMessage(
                      {
                        id: 'u_Xw8AcffYFnzJfpALpy',
                        defaultMessage: 'Credit you’ll earn',
                        description: 'Table header for credit earned amount in tour spend and earn modal',
                      },
                    )}
                  </BodyText>
                </TableBodyCell>
              </TableBodyRow>
              {spendEarnList.map(({ spend, earn }) => <TableBodyRow key={`${spend}-${earn}`}>
                <TableBodyCell><FormatCurrency value={spend} currency={currency} /></TableBodyCell>
                <TableBodyCell><FormatCurrency value={earn} currency={currency} /></TableBodyCell>
              </TableBodyRow>,
              )}
            </TableBody>
          </Table>
          <BodyTextBlock variant="medium">
            <FormattedMessage
              id="D5UxaX7EdW8ZRkJpXdaz"
              defaultMessage="Offer applies to {brandName} Tours only. Code can only be used once per customer. <link>Terms & conditions apply.</link>"
              description="Tour spend and earn modal offer terms body text with link to terms and conditions"
              values={{
                brandName: config.brandConfig.name,
                link: (chunks) => <TextLink onClick={onClose} to="/promotions" weight="regular">{chunks}</TextLink>,
              }}
            />
          </BodyTextBlock>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

export default TourSpendEarnDetailModal
